import { useMemo, useState } from 'react'
import moment from 'moment'
import to from 'await-catch'
import { useClinicsOptions } from '../../../hooks/useClinicsOptions'
import { useInstitutionOption } from '../../../hooks/useInstitutionOption'
import useIsMobile from '../../../hooks/useIsMobile'
import {
	cancelInvoiceSending,
	downloadInvoiceList,
	generateInvoicePackage,
	getDownloadLink,
	getInvoiceList,
	sendInvoice
} from '../../../api'
import { BILLING_GROUP, NOT_SENT, SENT } from '../../../constants'
import { getInvoiceSequence, parseInvoiceResults, mapInvoicesToRequest } from './invoiceHelpers'

export const useInvoice = ({ auth, showLoader, hideLoader, addFlashMessage, addFlashMessageSuccess }) => {
	const [resultsLst, setResultsLst] = useState([])
	const [totalPages, setTotalPages] = useState(1)
	const [currentPage, setCurrentPage] = useState(1)
	const [submitting, setSubmitting] = useState(false)
	const [searchWasMade, setSearchWasMade] = useState(false)
	const [sort, setSort] = useState({ filtro: '', orden: '' })
	const [selectedInvoices, setSelectedInvoices] = useState([])
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		action: null,
		row: null,
		submitting: false
	})

	const userRoles = auth?.gruposRoles || []
	const isBillingMode = userRoles.includes(BILLING_GROUP)

	const [localFilters, setLocalFilters] = useState({
		clinic: '',
		tipoComprobante: '',
		tipoFactura: '',
		centroFactura: '',
		numeroComprobante: '',
		cuit: '',
		startDate: moment().subtract(115, 'days'),
		endDate: moment(),
		sentStatus: isBillingMode ? [SENT, NOT_SENT] : [SENT],
		institution: '',
		id: '',
	})

	const isMobile = useIsMobile()
	const clinicsOptionsObj = useClinicsOptions({ showLoader, hideLoader, addFlashMessage })
	const clinicsOptions = Array.isArray(clinicsOptionsObj.clinicsOptions)
		? clinicsOptionsObj.clinicsOptions
		: []

	const institutionObj = useInstitutionOption({ showLoader, hideLoader, addFlashMessage })
	const institutionOptions = Array.isArray(institutionObj.institutionOptions)
		? institutionObj.institutionOptions
		: []

	const billingDesktopFieldWidth = !isMobile && isBillingMode ? 'calc(33.33% - 11px)' : undefined

	const applyFiltersAndFetch = async ({ values, page = 1, sentStatusOverride, sortOverride }) => {
		setSubmitting(true)
		setResultsLst([])

		const selectedInstitution = institutionObj.institutions.find(
			institution => String(institution.id) === String(values.institution || values.id || '')
		)

		const cuit =
			selectedInstitution?.cuit ||
			auth?.metadatos?.find(metadata => metadata.metadato.clave === 'CUIT')?.valor ||
			''
		const formatDate = date => (date && date.toISOString ? date.toISOString() : null)

		const nextFilters = {
			clinic: values.clinic,
			tipoComprobante: values.tipoComprobante,
			tipoFactura: values.tipoFactura,
			centroFactura: values.centroFactura,
			numeroComprobante: values.numeroComprobante,
			cuit,
			institution: values.institution || '',
			startDate: values.startDate,
			endDate: values.endDate,
			sentStatus: sentStatusOverride || values.sentStatus || localFilters.sentStatus,
			id: selectedInstitution?.id || values.id || '',
		}

		const nextSort = sortOverride || sort

		const params = {
			cuit,
			idClinica: values.clinic,
			tipoComprobante: values.tipoComprobante,
			tipoFactura: values.tipoFactura,
			centroFactura: values.centroFactura,
			numeroComprobante: values.numeroComprobante,
			fechaDesde: formatDate(values.startDate),
			fechaHasta: formatDate(values.endDate),
			pagina: page,
			itemsPorPagina: 10,
			id: selectedInstitution?.id || values.id || '',
			estadoEnvio: nextFilters.sentStatus,
			...(nextSort?.filtro && nextSort?.orden
				? { filtro: nextSort.filtro, orden: nextSort.orden }
				: {})
		}

		try {
			const [err, { data } = {}] = await to(getInvoiceList(params))
			if (!err && data && Array.isArray(data.items)) {
				setResultsLst(parseInvoiceResults(data.items))
				setTotalPages(data.totalPaginas || 1)
			} else {
				setResultsLst([])
				setTotalPages(1)
			}
			setLocalFilters(nextFilters)
			setCurrentPage(page)
			setSelectedInvoices([])
		} finally {
			setSubmitting(false)
			setSearchWasMade(true)
		}
	}

	const handlePageChange = page => {
		return applyFiltersAndFetch({
			values: {
				clinic: localFilters.clinic,
				id: localFilters.id,
				tipoComprobante: localFilters.tipoComprobante,
				tipoFactura: localFilters.tipoFactura,
				centroFactura: localFilters.centroFactura,
				numeroComprobante: localFilters.numeroComprobante,
				startDate: localFilters.startDate,
				endDate: localFilters.endDate,
				sentStatus: localFilters.sentStatus
			},
			page
		})
	}

	const handleSort = (filtro, orden) => {
		const nextSort = { filtro, orden }
		setSort(nextSort)
		applyFiltersAndFetch({
			values: {
				clinic: localFilters.clinic,
				institution: localFilters.institution,
				id: localFilters.id,
				tipoComprobante: localFilters.tipoComprobante,
				tipoFactura: localFilters.tipoFactura,
				centroFactura: localFilters.centroFactura,
				numeroComprobante: localFilters.numeroComprobante,
				startDate: localFilters.startDate,
				endDate: localFilters.endDate,
				sentStatus: localFilters.sentStatus
			},
			page: 1,
			sortOverride: nextSort
		})
	}

	const handleDownloadInvoicePackage = async row => {
		const clinicId = Number(row.idClinica)
		const invoiceSequence = getInvoiceSequence(row)
		const request = {
			centroFactura: row.centroFactura,
			modalidad: row.modalidad,
			idClinica: clinicId,
			numeroComprobante: row.numeroComprobante,
			tipoComprobante: row.tipoComprobante,
			tipoFactura: row.tipoFactura,
			idObraSocial: row.idFinanciador,
			cuit: localFilters.cuit,
			fecha: row.fecha,
			origen: row.origen,
			nrSequencia: invoiceSequence
		}

		if (Number.isNaN(clinicId)) {
			addFlashMessage('Ocurrió un error en la descarga')
			return
		}

		const [err, { data } = {}] = await to(generateInvoicePackage(request))
		if (err || !data?.token || !data?.file) {
			addFlashMessage('Ocurrió un error descargando el comprobante')
			return
		}

		const downloadUrl = getDownloadLink(data.token, data.file)
		const link = document.createElement('a')
		link.href = downloadUrl
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	const handleMassAction = async action => {
		showLoader()
		try {
			const sourceInvoices = selectedInvoices
			if (!sourceInvoices.length) {
				addFlashMessage('Debe seleccionar al menos un comprobante')
				return
			}

			const invoicesToProcess =
				action === 'send'
					? sourceInvoices.filter(item => !item.enviado)
					: sourceInvoices.filter(item => item.enviado && !item.descargado)

			if (!invoicesToProcess.length) {
				addFlashMessage('No hay comprobantes para procesar con ese estado')
				return
			}

			const request = mapInvoicesToRequest(invoicesToProcess)
			const [err] = await to(
				action === 'send' ? sendInvoice(request) : cancelInvoiceSending(request)
			)

			if (err) {
				addFlashMessage('Ocurrió un error al procesar la facturación')
				return
			}

			await handlePageChange(currentPage)
			addFlashMessageSuccess(
				action === 'send'
					? 'Todas las facturaciones fueron enviadas con éxito'
					: 'Todas las facturaciones fueron canceladas con éxito'
			)
		} finally {
			hideLoader()
		}
	}

	const handleRowAction = async (row, action) => {
		showLoader()
		try {
			const request = mapInvoicesToRequest([row])
			const [err] = await to(
				action === 'send' ? sendInvoice(request) : cancelInvoiceSending(request)
			)

			if (err) {
				addFlashMessage('Ocurrió un error al procesar el comprobante')
				return
			}

			await handlePageChange(currentPage)
			addFlashMessageSuccess(
				action === 'send' ? 'Comprobante enviado correctamente' : 'Envío cancelado correctamente'
			)
		} finally {
			hideLoader()
		}
	}

	const handleExportExcel = async () => {
		const request = {
			cuit: localFilters.cuit,
			fechaDesde: localFilters.startDate,
			fechaHasta: localFilters.endDate,
			tipoComprobante: localFilters.tipoComprobante,
			tipoFactura: localFilters.tipoFactura,
			centroFactura: localFilters.centroFactura,
			numeroComprobante: localFilters.numeroComprobante,
			idClinica: localFilters.clinic,
			...(isBillingMode ? { estadoEnvio: localFilters.sentStatus } : {})
		}

		const [err, { data } = {}] = await to(downloadInvoiceList(request))
		if (err) {
			addFlashMessage('Ocurrió un error exportando el listado')
			return
		}

		const blob = new Blob([data])
		const filename = `listado_comprobantes_${new Date().getTime()}.xls`

		if (window.navigator && window.navigator.msSaveOrOpenBlob) {
			window.navigator.msSaveBlob(blob, filename)
		} else {
			const link = document.createElement('a')
			link.href = window.URL.createObjectURL(blob)
			link.download = filename
			link.click()
		}
	}

	const openConfirmModal = (action, row = null) => {
		setConfirmModal({ show: true, action, row, submitting: false })
	}

	const closeConfirmModal = () => {
		setConfirmModal({ show: false, action: null, row: null, submitting: false })
	}

	const handleConfirmActionModal = async () => {
		if (!confirmModal.action) return
		setConfirmModal(prev => ({ ...prev, submitting: true }))
		try {
			if (confirmModal.row) {
				await handleRowAction(confirmModal.row, confirmModal.action)
			} else {
				await handleMassAction(confirmModal.action)
			}
		} finally {
			closeConfirmModal()
		}
	}

	const getConfirmModalTitle = () => {
		const { action, row } = confirmModal
		if (row) {
			return action === 'send'
				? `¿Desea enviar la facturación "${row.factura}"?`
				: `¿Desea cancelar el envío de la facturación "${row.factura}"?`
		}
		return action === 'send'
			? '¿Está seguro de querer enviar todas las facturaciones seleccionadas?'
			: '¿Está seguro de querer cancelar todas las facturaciones seleccionadas?'
	}

	const isInvoiceSelected = invoice =>
		selectedInvoices.some(selected => String(selected.id) === String(invoice.id))

	const toggleInvoiceSelection = invoice => {
		setSelectedInvoices(previous => {
			const exists = previous.some(selected => String(selected.id) === String(invoice.id))
			if (exists) {
				return previous.filter(selected => String(selected.id) !== String(invoice.id))
			}
			return [...previous, invoice]
		})
	}

	const invoiceFields = useMemo(() => {
		if (!isBillingMode) {
			return [
				{ key: 'factura', label: 'N° Factura' },
				{ key: 'fecha', label: 'Fecha' },
				{ key: 'importe', label: 'Importe' },
				{ key: 'acciones', label: 'Acciones' }
			]
		}

		return [
			{ key: 'financiador', label: 'Financiador' },
			{ key: 'factura', label: 'N° Factura' },
			{ key: 'importe', label: 'Importe' },
			{ key: 'descargadaEstado', label: 'Descargada' },
			{ key: 'fecha', label: 'Fecha' },
			{ key: 'acciones', label: 'Acciones' }
		]
	}, [isBillingMode])

	return {
		resultsLst,
		searchWasMade,
		setSelectedInvoices,
		totalPages,
		currentPage,
		submitting,
		selectedInvoices,
		confirmModal,
		isBillingMode,
		localFilters,
		isMobile,
		clinicsOptions,
		institutionOptions,
		institutions: institutionObj.institutions,
		billingDesktopFieldWidth,
		invoiceFields,
		applyFiltersAndFetch,
		handlePageChange,
		handleSort,
		handleDownloadInvoicePackage,
		handleMassAction,
		handleRowAction,
		handleExportExcel,
		openConfirmModal,
		closeConfirmModal,
		handleConfirmActionModal,
		getConfirmModalTitle,
		isInvoiceSelected,
		toggleInvoiceSelection
	}
}
